// The signed-in staff member's role, made available to every admin page.
//
// A context rather than props threaded through each page, because almost every
// admin screen needs it and the ones in the middle of the tree do not otherwise
// care. It is populated once from /api/admin/whoami after sign-in.
//
// Worth being clear about what this is for: it decides what the portal *shows*.
// It is not the enforcement. Every admin route checks the capability server-side
// and answers 403 regardless of what the browser rendered, so a staff member who
// edits their own JavaScript gains nothing. Hiding a control they cannot use is a
// courtesy to them, not a security boundary.
import { createContext, useContext, type ReactNode } from 'react';
import type { Capability, Whoami } from './api';

const StaffContext = createContext<Whoami | undefined>(undefined);

export function StaffProvider({ staff, children }: { staff: Whoami; children: ReactNode }) {
  return <StaffContext.Provider value={staff}>{children}</StaffContext.Provider>;
}

// Returns undefined for a partner session, where there is no staff role at all.
export const useStaff = () => useContext(StaffContext);

// Defaults to false when there is no staff context. That is the safe direction:
// a partner rendering an admin control by mistake shows nothing rather than
// showing a button that 403s.
export const useCan = (capability: Capability): boolean =>
  useStaff()?.capabilities.includes(capability) ?? false;

export const ROLE_LABELS: Record<string, string> = {
  admin: 'admin',
  support: 'support',
  viewer: 'view only',
};

// Shown wherever a control has been hidden, so a viewer understands the screen is
// complete rather than broken. Named after the role, because "ask an
// administrator" is only actionable if they know what they currently are.
export function RoleNote({ what }: { what: string }) {
  const staff = useStaff();
  if (!staff) return null;
  return (
    <p className="hint">
      Your role ({ROLE_LABELS[staff.role] ?? staff.role}) cannot {what}. Ask an administrator if you
      need to.
    </p>
  );
}
